// src/hooks/useInactivityTimeout.js
import { useEffect, useRef, useCallback } from 'react';

const useInactivityTimeout = (onTimeout, timeout = 30 * 60 * 1000) => { // 30 minutes default
  const timeoutRef = useRef(null);
  const isActiveRef = useRef(true);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        onTimeout();
      }
    }, timeout);
  }, [onTimeout, timeout]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      isActiveRef.current = false;
    };
  }, [handleActivity, resetTimer]);

  // Function to manually reset the timer (useful for API calls)
  const resetTimeout = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  return { resetTimeout };
};

export default useInactivityTimeout;